// Page-context dataLayer helpers.
//
// Single source of truth is ./config.json (the centralized config). Per the
// GA4 measurement foundation, every page emits a fixed set of page-context
// values so GTM can segment by indication, content pillar, journey stage,
// language, and audience without scraping the DOM.
//
// Two emission points consume this module:
//   1. pageContextInitScript() -> inline <script> injected in the document
//      head, before the GTM snippet (initial-load push).
//   2. pushVirtualPageView()   -> SPA virtual pageview on Gatsby onRouteUpdate.

import config from "./config.json";

const { globals, sites } = config;

/**
 * Normalize a pathname to the trailing-slash form used as the route key.
 * Root ("/") is left untouched.
 */
const normalizePath = (pathname) => {
  if (!pathname) return "/";
  if (pathname.length > 1 && pathname.charAt(pathname.length - 1) !== "/") {
    return pathname + "/";
  }
  return pathname;
};

/**
 * Resolve the flat page-context object for a given site + pathname.
 * Unmapped routes (404s, anything content has not classified) fall back to
 * the site's fallback values.
 */
export const resolvePageContext = (siteKey, pathname) => {
  const site = sites[siteKey];
  if (!site) {
    // Unknown site key: still emit the global fields so the contract holds.
    return {
      page_language: globals.page_language,
      site_version: globals.site_version,
    };
  }

  const route = site.routes[normalizePath(pathname)] || site.fallback;

  return {
    indication: site.indication,
    audience_type: site.audience_type,
    content_pillar: route.content_pillar,
    journey_stage: route.journey_stage,
    page_type: route.page_type,
    page_language: globals.page_language,
    site_version: globals.site_version,
  };
};

/**
 * Push a virtual pageview for an SPA route change. Call from onRouteUpdate.
 */
export const pushVirtualPageView = (siteKey, pathname) => {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: "virtual_page_view",
    ...resolvePageContext(siteKey, pathname),
  });
};

/**
 * Build a self-contained inline script (string) for the document head.
 * It serializes the site's config and resolves the current route at runtime,
 * then pushes the page-context object onto window.dataLayer synchronously,
 * before the async GTM snippet executes. Insert as the FIRST head component.
 */
export const pageContextInitScript = (siteKey) => {
  const site = sites[siteKey] || {};
  const payload = {
    indication: site.indication ?? null,
    audience_type: site.audience_type ?? null,
    page_language: globals.page_language,
    site_version: globals.site_version,
    fallback: site.fallback ?? {
      page_type: "other",
      content_pillar: "none",
      journey_stage: "none",
    },
    routes: site.routes ?? {},
  };

  return (
    "(function(){" +
    "var d=" +
    JSON.stringify(payload) +
    ";" +
    'var p=(window.location.pathname)||"/";' +
    'if(p.length>1&&p.charAt(p.length-1)!=="/"){p+="/";}' +
    "var r=d.routes[p]||d.fallback;" +
    "window.dataLayer=window.dataLayer||[];" +
    "window.dataLayer.push({" +
    "indication:d.indication," +
    "audience_type:d.audience_type," +
    "content_pillar:r.content_pillar," +
    "journey_stage:r.journey_stage," +
    "page_type:r.page_type," +
    "page_language:d.page_language," +
    "site_version:d.site_version" +
    "});})();"
  );
};
